/**
 * Shifts View Page — read-only detail.
 */

import * as React from 'react';
import { useParams, useNavigate } from 'react-router';
import { useOne } from '@refinedev/core';
import { Button, Card, CardContent, CardHeader, CardTitle, Badge } from '@yesbos/ui';
import { ArrowLeft, Edit } from 'lucide-react';

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <dt className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{label}</dt>
      <dd className="text-sm font-medium">{value ?? "—"}</dd>
    </div>
  );
}

export function ShiftsViewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading } = useOne({ resource: 'shifts', id: id! });
  const shift = data?.data as Record<string, unknown> | undefined;

  if (isLoading) return <div className="p-6 text-sm text-muted-foreground">Loading...</div>;
  if (!shift) return <div className="p-6 text-sm text-destructive">Shift not found.</div>;

  return (
    <div className="p-6 max-w-xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/shifts')}><ArrowLeft className="h-4 w-4" /></Button>
          <h1 className="text-2xl font-semibold">{String(shift.name ?? '')}</h1>
          {shift.is_night_shift && <Badge variant="secondary">Night Shift</Badge>}
        </div>
        <Button variant="outline" onClick={() => navigate(`/shifts/${id}/edit`)}>
          <Edit className="h-4 w-4 mr-2" />Edit
        </Button>
      </div>
      <Card>
        <CardHeader><CardTitle>Shift Details</CardTitle></CardHeader>
        <CardContent>
          <dl className="grid grid-cols-2 gap-4">
            <Field label="Name" value={String(shift.name ?? '')} />
            <Field label="Type" value={shift.is_night_shift ? 'Night Shift' : 'Day Shift'} />
            <Field label="Start Time" value={String(shift.start_time ?? '')} />
            <Field label="End Time" value={String(shift.end_time ?? '')} />
            <Field label="Break Duration" value={`${shift.break_duration ?? 60} minutes`} />
          </dl>
        </CardContent>
      </Card>
    </div>
  );
}

export default ShiftsViewPage;
